import React, { ReactElement, useState } from 'react';
import { Button, Dropdown, Modal } from 'react-bootstrap';
import { Colonne } from '../../Utils/TypesTableSimple';
import { MyTable } from '../../Utils/TableSimple';
import { MyPaper } from '../../Utils/MyPaper';
import ActiviteEntenteProduit from '../../../../../../common/src/entities/activite-entente-produit.entity';
import { CreerActiviteEntenteProduit } from './CreerActiviteEntenteProduit';
import DetailsActiviteEntenteProduit from './DetailsActiviteEntenteProduit';
import { getTotalCommissionFacture, getTotalSurfacturationFacture } from '../../Produits/UtilsFunctionsProduits';
import { NombreLog } from '../../../../../../common/src/entities/nombre-log.entity';
import DetailsQuantitesActiviteEntenteProduit from './DetailsQuantitesActiviteEntenteProduit';
import { useFormikContext } from 'formik';
import { Entente } from '../../../../../../common/src/entities';

const AffichageAjout = (props: {
    addNewActiviteEntenteProduit: (newItem: ActiviteEntenteProduit) => void;
}): ReactElement => {
    const [showModal, setShowModal] = useState(false);
    const { values } = useFormikContext<Entente>();
    return (
        <>
            <Button variant="outline-primary" size="sm" onClick={() => setShowModal(true)}>
                Ajouter un produit
            </Button>
            <Modal
                scrollable
                show={showModal}
                onHide={() => {
                    setShowModal(false);
                }}
            >
                <CreerActiviteEntenteProduit
                    entente={values}
                    indexActiviteEntenteProduit={
                        values.activiteEntentes
                            ? values.activiteEntentes.map((ae) => (ae ? ae.activiteEntenteProduit : [])).flat().length
                            : 0
                    }
                    succes={(produit: any) => {
                        props.addNewActiviteEntenteProduit(produit);
                        setShowModal(false);
                    }}
                />
            </Modal>
        </>
    );
};

export type TableActiviteEntenteProduitProps = {
    onModify?: (activiteIndex, produitIndex: number, data: ActiviteEntenteProduit) => void;
    onAdd?: (data: ActiviteEntenteProduit) => void;
    onRemove?: (indexActivite: number, indexProduit: number) => void;
};

export default function TableActiviteEntenteProduit({
    onModify,
    onAdd,
    onRemove,
}: TableActiviteEntenteProduitProps): ReactElement {
    const [showModal, setShowModal] = useState(false);
    const [showModalQuantites, setShowModalQuantites] = useState(false);
    const [produitIndex, setProduitIndex] = useState<number>(0);
    const [activiteIndex, setActiviteIndex] = useState<number | undefined>(undefined);
    const { values, setFieldValue } = useFormikContext<Entente>();

    const open = (data: ActiviteEntenteProduit, index: number) => {
        setProduitIndex(index);
        setActiviteIndex(values.activiteEntentes.findIndex((ae) => ae.id == data.activiteEntente.id));
        setShowModal(true);
    };

    const modify = (data: ActiviteEntenteProduit) => {
        setShowModal(false);
        if (onModify) onModify(activiteIndex, produitIndex, data);
    };

    const add = (data: any) => {
        if (onAdd) onAdd(data);
    };

    const remove = (data: ActiviteEntenteProduit, indexProduit: number) => {
        let indexActivite = values.activiteEntentes.findIndex((ae) => ae.id == data.activiteEntente.id);
        if (onRemove) onRemove(indexActivite, indexProduit);
    };

    const openQuantites = () => {
        setShowModalQuantites(true);
    };

    const colonnes: Colonne<ActiviteEntenteProduit>[] = [
        {
            Header: 'Fournisseur',
            accessor: (row) => {
                return values.activiteEntentes?.find((ae) => ae?.id === row.activiteEntente?.id)?.activite?.fournisseur
                    ?.name;
            },
            disableSortBy: true,
        },
        {
            Header: 'Activité',
            accessor: (row) => {
                return values.activiteEntentes?.find((ae) => ae?.id === row.activiteEntente?.id)?.activite?.name;
            },
            disableSortBy: true,
        },
        {
            Header: 'Produit',
            accessor: (row) => {
                return row.produit?.name;
            },
            disableSortBy: true,
        },
        {
            Header: 'Méthode facturation',
            accessor: (row) => {
                return row.facturation?.methode_facturation;
            },
            disableSortBy: true,
        },
        {
            Header: 'Quantité affichée au client',
            accessor: (row) => {
                return 1;
            },
            disableSortBy: true,
        },
        {
            Header: '$ client',
            accessor: (row) => {
                const facturation = row.facturation;
                if (facturation) {
                    const totalSurfacturation = getTotalSurfacturationFacture(facturation);
                    return (totalSurfacturation + Number(facturation.prix_de_vente_fournisseur)).toFixed(2);
                }
                return 0;
            },
            disableSortBy: true,
        },
        {
            Header: 'total client',
            accessor: (row) => {
                const facturation = row.facturation;
                if (facturation) {
                    const totalSurfacturation = getTotalSurfacturationFacture(facturation);
                    const prixClient = totalSurfacturation + Number(facturation.prix_de_vente_fournisseur);
                    return (prixClient * 10).toFixed(2); // 10 est le nombre de produit Hardcoded
                }
                return 0;
            },
            disableSortBy: true,
        },
        {
            Header: 'Quantité affichée au fournisseur',
            accessor: (row) => {
                return 2;
            },
            disableSortBy: true,
        },
        {
            Header: '$ fournisseur',
            accessor: (row) => {
                return row.facturation?.prix_de_vente_fournisseur;
            },
            disableSortBy: true,
        },
        {
            Header: 'total fournisseur',
            accessor: (row) => {
                return Number(row.facturation?.prix_de_vente_fournisseur) * 10; // 10 est le nombre de produit Hardcoded
            },
            disableSortBy: true,
        },
        {
            Header: '$ profit',
            accessor: (row) => {
                const facturation = row.facturation;
                if (facturation) {
                    return ((getTotalCommissionFacture(facturation) + getTotalSurfacturationFacture(facturation)) * 10) // 10 est le nombre de produit Hardcoded
                        .toFixed(2);
                }
            },
            disableSortBy: true,
        },
    ];

    function cleanNombreLogs() {
        const newActiviteEntentes =
            values.activiteEntentes &&
            values.activiteEntentes.map((ae) => ({
                ...ae,
                activiteEntenteProduit:
                    ae.activiteEntenteProduit &&
                    ae.activiteEntenteProduit.map((aep) => ({
                        ...aep,
                        nombreLogs:
                            aep.nombreLogs &&
                            aep.nombreLogs.map((nl) => ({
                                ...nl,
                                fk_id_activite_entente_produit: undefined,
                            })),
                    })),
            }));
        setFieldValue('activiteEntentes', newActiviteEntentes);
    }

    return (
        <MyPaper>
            <MyTable
                colonnes={colonnes}
                data={
                    values.activiteEntentes
                        ? values.activiteEntentes
                              .map((ae) => (ae ? ae.activiteEntenteProduit : []))
                              .flat()
                              .map((aep) => ({ ...aep }))
                              .filter(function (element) {
                                  return element !== null && element !== undefined;
                              })
                        : []
                }
                lien="id"
                hover
                affichageMenu={(activiteEntenteProduit: ActiviteEntenteProduit, index: number) => (
                    <>
                        <Dropdown.Item onClick={() => open(activiteEntenteProduit, index)}>Modifier</Dropdown.Item>
                        <Dropdown.Item
                            onClick={() => {
                                remove(activiteEntenteProduit, index);
                            }}
                            className="text-danger"
                        >
                            Supprimer
                        </Dropdown.Item>
                    </>
                )}
                cantNavigate={false}
            />
            <Modal
                scrollable
                show={showModal}
                onHide={() => {
                    setShowModal(false);
                }}
            >
                <DetailsActiviteEntenteProduit
                    entente={values}
                    indexActiviteEntente={activiteIndex}
                    indexActiviteEntenteProduit={produitIndex}
                    onBeforeSave={(data: ActiviteEntenteProduit) => modify(data)}
                    succes={(produit: any) => {
                        modify(produit);
                        setShowModal(false);
                    }}
                />
            </Modal>
            <Modal
                scrollable
                show={showModalQuantites}
                onHide={() => {
                    setShowModalQuantites(false);
                }}
                size="xl"
            >
                <DetailsQuantitesActiviteEntenteProduit
                    onConfirm={() => {
                        setShowModalQuantites(false);
                    }}
                />
            </Modal>
            <hr /> {/* Ligne de séparation */}
            <AffichageAjout addNewActiviteEntenteProduit={add} />{' '}
            <Button variant="outline-primary" size="sm" onClick={() => openQuantites()}>
                Quantités
            </Button>
        </MyPaper>
    );
}
